/** What a screen says when the workspace has no reporting period to read figures for.
 *
 * Every figure in this product is for a window, and `useScreen` now reports honestly when there
 * is none (`screen.window` is `undefined`) instead of handing over a 1970 date. The data hooks
 * refuse to issue a request for it, so a screen has to say something in the place its figures
 * would be — **design rule 5**, an absence is said, never left blank. A panel reading *"No figures
 * for this period"* would be the other failure: a claim about a period nobody has chosen.
 *
 * **One component for the screens that need it**, because the sentence and the role check are
 * the parts that must not differ between them. Where the same fact appears on two screens it is
 * written once (design rule 7).
 *
 * **It looks like every other empty place** (KD, 2026-11-02): the page keeps its title card, the tiles
 * it would count show a quiet dash (`DashTiles`), and the table card holds one designed `EmptyState` -
 * an icon, a headline, one short sentence and one action. It used to be a bare card with no title
 * card above it, so a brand-new workspace's first look at Scope 1 was a different page from every
 * other empty one.
 *
 * **Who can fix it is told, not offered** (design rule 6). A reporting period is created on the
 * Facilities screen, which is `MAY_ADMINISTER` on the server, so the link is rendered for an
 * Admin and everybody else is told whose job it is. A link a Contributor follows to a screen that
 * then says "you can't" is a control that predictably fails, one click later.
 */

import { CalendarBlank } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useMe } from "../api/auth";
import { EmptyAction } from "../components/ui/EmptyAction";
import { EmptyState } from "../components/ui/EmptyState";
import { InfoPopover } from "../components/ui/InfoPopover";
import { TablePage } from "../components/ui/TablePage";
import { DashTiles } from "../components/ui/Tile";
import { locate } from "../shell/routeTable";
import { ScreenFrame } from "./ScreenFrame";
import own from "./NoPeriod.module.css";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import type { Screen, Window } from "./useScreen";
import { useScreen } from "./useScreen";

/** The panel: one designed empty state, for the table card of a page that is already inside its own
 * `TablePage` (or any card). One action for the person who can set a period up, and for everybody
 * else one sentence saying whose job it is. The reasons nothing is shown instead of a total are behind
 * the info control, none deleted. */
export function NoPeriod() {
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  return (
    <div className={own.card} data-testid="no-period">
      <EmptyState
        icon={CalendarBlank}
        title="No reporting period yet"
        compact
        action={
          <span className={own.action}>
            <EmptyAction
              label="Set up a company"
              allowed={me ? isAdmin : undefined}
              reason="An Admin sets up reporting periods."
              to="/facilities"
              search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "companies" })}
              plus={false}
              testId="no-period-set-up"
              reasonTestId="no-period-who-can"
            />
            <InfoPopover infoKey="scope1.no-period" />
          </span>
        }
        testId="no-period-empty"
      >
        Figures are for a reporting period.
      </EmptyState>
    </div>
  );
}

/** A whole screen that has nothing to show without a period — the title card, a strip of dashes, the
 * panel.
 *
 * For a screen whose every panel is a view of one window (the register, the scope screens, the
 * census, bulk edit). A screen that has panels unrelated to the window uses `NoPeriod` inside its
 * own frame instead, and keeps the rest.
 *
 * The icon is the place's own (the route table's), so the card above the panel is the one the page
 * wears with a period. `tiles` names what the page will count, three to five labels.
 */
export function NoPeriodScreen({
  screen,
  title,
  crumb,
  tiles,
}: {
  screen: Screen;
  title: string;
  crumb?: string;
  tiles?: readonly string[];
}) {
  const { pathname } = useLocation();
  const place = locate(pathname, screen.tab);
  const icon: Icon = place?.nav.icon ?? CalendarBlank;
  return (
    <ScreenFrame screen={screen} title={title} crumb={crumb}>
      <TablePage
        icon={icon}
        title={title}
        label={crumb}
        tiles={tiles && tiles.length >= 3 ? <DashTiles labels={tiles} label={`${title} summary`} /> : undefined}
        state={<NoPeriod />}
      />
    </ScreenFrame>
  );
}

/** Hand a screen its window, or say there is none — so a body below this **cannot** be rendered
 * without one.
 *
 * The point is the type: `children` receives a `Window`, not a `Window | undefined`, so a screen
 * written under this has no branch to forget. The alternative — each body checking `screen.window`
 * itself — is five copies of a guard that has to sit after every hook and before every figure, and
 * the first copy to be missed renders `0.0 tCO₂e` for a period nobody chose.
 *
 * `title` and `crumb` are for the frame **this** renders when there is no window; the body renders
 * its own when there is, and the two must name the same screen.
 */
export function RequirePeriod({
  title,
  crumb,
  tiles,
  children,
}: {
  title: string;
  crumb?: string;
  /** What the page counts, for the strip of dashes shown while there is no period (3 to 5 labels). */
  tiles?: readonly string[];
  children: (screen: Screen, period: Window) => ReactNode;
}) {
  const screen = useScreen();
  if (!screen.window)
    return <NoPeriodScreen screen={screen} title={title} crumb={crumb} tiles={tiles} />;
  return <>{children(screen, screen.window)}</>;
}
